import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { roleDisplayLabel } from "@/lib/auth";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Search, Filter, RefreshCw, Clock, User as UserIcon, FileText,
  Shield, LogIn, KeyRound, AlertCircle, ArrowRight,
} from "lucide-react";

const ACTION_COLORS = {
  login: "bg-blue-50 text-blue-800 border border-blue-200",
  login_failed: "bg-red-50 text-red-700 border border-red-200",
  logout_all_self: "bg-gray-100 text-gray-700 border border-gray-200",
  password_reset: "bg-amber-50 text-amber-800 border border-amber-200",
  user_create: "bg-green-50 text-green-800 border border-green-200",
  user_update: "bg-blue-50 text-blue-800 border border-blue-200",
  user_delete: "bg-red-50 text-red-700 border border-red-200",
  store_create: "bg-green-50 text-green-800 border border-green-200",
  store_update: "bg-blue-50 text-blue-800 border border-blue-200",
  store_delete: "bg-red-50 text-red-700 border border-red-200",
  zone_create: "bg-green-50 text-green-800 border border-green-200",
  zone_rename: "bg-blue-50 text-blue-800 border border-blue-200",
  zone_delete: "bg-red-50 text-red-700 border border-red-200",
  vendor_create: "bg-green-50 text-green-800 border border-green-200",
  vendor_update: "bg-blue-50 text-blue-800 border border-blue-200",
  vendor_delete: "bg-red-50 text-red-700 border border-red-200",
  po_create: "bg-green-50 text-green-800 border border-green-200",
  po_update: "bg-blue-50 text-blue-800 border border-blue-200",
  po_delete: "bg-red-50 text-red-700 border border-red-200",
  invoice_create: "bg-green-50 text-green-800 border border-green-200",
  invoice_update: "bg-blue-50 text-blue-800 border border-blue-200",
  invoice_delete: "bg-red-50 text-red-700 border border-red-200",
  expense_create: "bg-gray-50 text-gray-800 border border-gray-200",
  expense_approved: "bg-green-50 text-green-800 border border-green-200",
  expense_rejected: "bg-red-50 text-red-700 border border-red-200",
  expense_delete: "bg-red-50 text-red-700 border border-red-200",
  sale_unlock: "bg-amber-50 text-amber-800 border border-amber-200",
};

const ROLE_ICON = { Admin: Shield, Supervisor: UserIcon, Promoter: UserIcon, Client: UserIcon, system: Shield };

function timeSince(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AdminAudit() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [limited, setLimited] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [entities, setEntities] = useState([]);

  const [filter, setFilter] = useState({
    q: "", action: "", entity_type: "", from_date: "", to_date: "",
  });
  const [limit, setLimit] = useState(200);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (filter.q) params.q = filter.q;
      if (filter.action) params.action = filter.action;
      if (filter.entity_type) params.entity_type = filter.entity_type;
      if (filter.from_date) params.from_date = filter.from_date;
      if (filter.to_date) params.to_date = filter.to_date;
      const [{ data }, { data: opts }] = await Promise.all([
        api.get("/audit-log", { params }),
        api.get("/audit-log/actions"),
      ]);
      setRows(data.rows); setCount(data.count); setLimited(data.limited);
      setActions(opts.actions); setEntities(opts.entity_types);
    } catch { toast.error("Failed to load audit log"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.action, filter.entity_type, filter.from_date, filter.to_date, limit]);

  const applySearch = (e) => {
    if (e.key === "Enter") load();
  };

  const reset = () => setFilter({ q: "", action: "", entity_type: "", from_date: "", to_date: "" });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="Audit Log" subtitle="Every login, edit, approval and reset — searchable" />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">

          {/* Filters */}
          <Card className="rounded-xl border-gray-200 bg-white p-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[220px] max-w-sm">
                <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="audit-search" placeholder="Search actor, entity, action…" value={filter.q}
                  onChange={e => setFilter({ ...filter, q: e.target.value })} onKeyDown={applySearch} className="pl-9" />
              </div>
              <select data-testid="audit-action-filter" value={filter.action}
                onChange={e => setFilter({ ...filter, action: e.target.value })}
                className="h-10 rounded-md border border-gray-200 px-3 text-sm bg-white">
                <option value="">All actions</option>
                {actions.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <select data-testid="audit-entity-filter" value={filter.entity_type}
                onChange={e => setFilter({ ...filter, entity_type: e.target.value })}
                className="h-10 rounded-md border border-gray-200 px-3 text-sm bg-white">
                <option value="">All entities</option>
                {entities.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
              <Input data-testid="audit-from-date" type="date" value={filter.from_date}
                onChange={e => setFilter({ ...filter, from_date: e.target.value })} className="w-40" />
              <Input data-testid="audit-to-date" type="date" value={filter.to_date}
                onChange={e => setFilter({ ...filter, to_date: e.target.value })} className="w-40" />
              <select data-testid="audit-limit-select" value={limit} onChange={e => setLimit(Number(e.target.value))}
                className="h-10 rounded-md border border-gray-200 px-3 text-sm bg-white">
                {[100, 200, 500, 1000].map(n => <option key={n} value={n}>{n} rows</option>)}
              </select>
              <div className="ml-auto flex items-center gap-2">
                <Button data-testid="audit-reset-btn" variant="outline" onClick={reset}>
                  <Filter className="h-4 w-4 mr-2" /> Reset
                </Button>
                <Button data-testid="audit-refresh-btn" variant="outline" onClick={load}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                </Button>
              </div>
            </div>
          </Card>

          <div className="flex items-center justify-between text-sm text-gray-600">
            <div><span className="font-semibold text-gray-900">{count}</span> event{count === 1 ? "" : "s"}{limited && " (result truncated — narrow the filter)"}</div>
          </div>

          {/* Timeline */}
          <Card className="rounded-xl border-gray-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-3 w-40">When</th>
                    <th className="text-left px-4 py-3">Actor</th>
                    <th className="text-left px-4 py-3">Action</th>
                    <th className="text-left px-4 py-3">Entity</th>
                    <th className="text-left px-4 py-3">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={5} className="text-center py-8 text-gray-500">Loading…</td></tr>}
                  {!loading && rows.map(r => {
                    const Icon = ROLE_ICON[r.actor_role] || UserIcon;
                    const badgeClass = ACTION_COLORS[r.action] || "bg-gray-100 text-gray-800 border border-gray-200";
                    return (
                      <tr key={r.id} className="border-t border-gray-100" data-testid={`audit-row-${r.id.slice(0,8)}`}>
                        <td className="px-4 py-3">
                          <div className="text-sm">{r.at.slice(0, 19).replace("T", " ")}</div>
                          <div className="text-xs text-gray-400 flex items-center gap-1"><Clock className="h-3 w-3" />{timeSince(r.at)}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="w-7 h-7 rounded-full bg-red-50 text-brand-red flex items-center justify-center">
                              <Icon className="h-4 w-4" />
                            </span>
                            <div>
                              <div className="font-semibold text-sm">{r.actor_name}</div>
                              <div className="text-[11px] uppercase tracking-wider text-gray-500">{roleDisplayLabel(r.actor_role)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${badgeClass} font-mono text-[11px] font-semibold`}>{r.action}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">{r.entity_label || <span className="text-gray-400">—</span>}</div>
                          <div className="text-[11px] text-gray-500 uppercase tracking-wider">{r.entity_type}</div>
                        </td>
                        <td className="px-4 py-3">
                          {r.meta && Object.keys(r.meta).length > 0 && (
                            <div className="text-xs text-gray-600 space-x-2">
                              {Object.entries(r.meta).map(([k, v]) => {
                                if (v === null || v === undefined || v === "") return null;
                                const s = typeof v === "object" ? JSON.stringify(v) : String(v);
                                return <span key={k} className="inline-block bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 mr-1 mb-1"><span className="text-gray-500">{k}:</span> <span className="font-mono">{s.length > 60 ? s.slice(0, 57) + "…" : s}</span></span>;
                              })}
                            </div>
                          )}
                          {r.ip && <div className="text-[10px] text-gray-400 font-mono mt-0.5">ip {r.ip}</div>}
                        </td>
                      </tr>
                    );
                  })}
                  {!loading && rows.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-10 text-gray-500">
                      <AlertCircle className="h-6 w-6 mx-auto mb-2 text-gray-300" />
                      No audit events match your filters
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="text-xs text-gray-500">
            Tracks: <span className="font-mono">login · login_failed · password_reset · user/store/zone/vendor CRUD · PO/invoice CRUD · expense approvals · sale unlock</span>
          </div>
        </main>
      </div>
    </div>
  );
}
